import * as signalR from "@microsoft/signalr";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LiveTrackEvent } from "../types";

interface UseLiveTrackingMotionArgs {
  connectionToken?: string;
  fallbackTrackEvent?: LiveTrackEvent;
  initialTrackEvent?: LiveTrackEvent;
  vehicleId?: number;
}

interface InternalTrackEvent {
  lat: number;
  lng: number;
  bearing: number;
  speed: number;
  driverName?: string | null;
  trailers?: string | null;
  geolocation?: string | null;
  timestamp?: string;
}

interface MotionTrackEvent extends InternalTrackEvent {
  receivedAtMs: number;
}

interface CorrectionState {
  durationMs: number;
  startAtMs: number;
  startEvent: InternalTrackEvent;
}

interface UseLiveTrackingMotionResult {
  trackingLocation?: LiveTrackEvent;
  trailPath: TrailPoint[];
  updateReceived: boolean;
  predictionExpired: boolean;
  connectionTimedOut: boolean;
}

interface TrailPoint {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_METERS = 6_371_000;
const LIVE_TRACKING_MAX_PREDICTION_WINDOW_MS = 15_000;
const LIVE_TRACKING_INITIAL_WAIT_WINDOW_MS = 60_000;
const LIVE_TRACKING_INITIAL_RETRY_MS = 5_000;
const MAX_REASONABLE_SPEED_MPH = 130;
const MIN_MOVING_SPEED_MPH = 1;
const MIN_BEARING_DISTANCE_METERS = 5;
const MAX_TRAIL_POINTS = 250;
const MAX_CONSECUTIVE_BEHIND_PACKETS = 12;
const POSITION_EPSILON = 0.0000001;
const BEARING_EPSILON = 0.1;
const SPEED_EPSILON = 0.1;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const toRadians = (value: number) => (value * Math.PI) / 180;

const toDegrees = (value: number) => (value * 180) / Math.PI;

const normalizeLongitude = (value: number) => {
  const normalized = ((value + 540) % 360) - 180;
  return normalized === -180 ? 180 : normalized;
};

const normalizeBearingValue = (
  value?: number | null,
  fallback = 0
): number => {
  if (!isFiniteNumber(value)) {
    return fallback;
  }

  const normalized = value % 360;
  return normalized < 0 ? normalized + 360 : normalized;
};

const normalizeSpeedValue = (
  value?: number | null,
  fallback = 0
): number => {
  if (!isFiniteNumber(value) || value < 0) {
    return fallback;
  }

  return Math.min(value, MAX_REASONABLE_SPEED_MPH);
};

const normalizeTimestamp = (value?: string) => {
  if (!value) {
    return undefined;
  }

  return Number.isNaN(Date.parse(value)) ? undefined : value;
};

const getTimestampMs = (value?: string) => {
  if (!value) {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeTrackEvent = (
  event?: LiveTrackEvent,
  previousEvent?: Partial<InternalTrackEvent>
): InternalTrackEvent | undefined => {
  if (!event) {
    return undefined;
  }

  const lat = event.lat;
  const lng = event.lng;
  if (
    !isFiniteNumber(lat) ||
    !isFiniteNumber(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return undefined;
  }

  return {
    lat,
    lng,
    bearing: normalizeBearingValue(previousEvent?.bearing ?? 0),
    speed: normalizeSpeedValue(event.speed, previousEvent?.speed ?? 0),
    driverName: event.driverName ?? previousEvent?.driverName ?? null,
    trailers: event.trailers ?? previousEvent?.trailers ?? null,
    geolocation: event.geolocation ?? previousEvent?.geolocation ?? null,
    timestamp: normalizeTimestamp(event.timestamp) ?? previousEvent?.timestamp,
  };
};

const toLiveTrackEvent = (event?: InternalTrackEvent): LiveTrackEvent | undefined =>
  event
    ? {
      lat: event.lat,
      lng: event.lng,
      bearing: event.bearing,
      speed: event.speed,
      driverName: event.driverName,
      trailers: event.trailers,
      geolocation: event.geolocation,
      timestamp: event.timestamp,
    }
    : undefined;

const hasLiveTrackChanged = (
  previousValue?: LiveTrackEvent,
  nextValue?: LiveTrackEvent
) => {
  if (!previousValue && !nextValue) {
    return false;
  }

  if (!previousValue || !nextValue) {
    return true;
  }

  return (
    Math.abs((previousValue.lat ?? 0) - (nextValue.lat ?? 0)) > POSITION_EPSILON ||
    Math.abs((previousValue.lng ?? 0) - (nextValue.lng ?? 0)) > POSITION_EPSILON ||
    Math.abs((previousValue.bearing ?? 0) - (nextValue.bearing ?? 0)) >
    BEARING_EPSILON ||
    Math.abs((previousValue.speed ?? 0) - (nextValue.speed ?? 0)) > SPEED_EPSILON ||
    previousValue.driverName !== nextValue.driverName ||
    previousValue.trailers !== nextValue.trailers ||
    previousValue.geolocation !== nextValue.geolocation ||
    previousValue.timestamp !== nextValue.timestamp
  );
};

const easeOutCubic = (value: number) => 1 - Math.pow(1 - value, 3);

const getShortestBearingDelta = (fromBearing: number, toBearing: number) => {
  const delta = (toBearing - fromBearing + 540) % 360 - 180;
  return delta === -180 ? 180 : delta;
};

const interpolateTrackEvent = (
  startEvent: InternalTrackEvent,
  endEvent: InternalTrackEvent,
  progress: number
): InternalTrackEvent => ({
  lat: startEvent.lat + (endEvent.lat - startEvent.lat) * progress,
  lng: startEvent.lng + (endEvent.lng - startEvent.lng) * progress,
  bearing: normalizeBearingValue(
    startEvent.bearing +
    getShortestBearingDelta(startEvent.bearing, endEvent.bearing) * progress
  ),
  speed: startEvent.speed + (endEvent.speed - startEvent.speed) * progress,
  driverName: endEvent.driverName ?? startEvent.driverName ?? null,
  trailers: endEvent.trailers ?? startEvent.trailers ?? null,
  geolocation: endEvent.geolocation ?? startEvent.geolocation ?? null,
  timestamp: endEvent.timestamp ?? startEvent.timestamp,
});

const projectCoordinates = (
  lat: number,
  lng: number,
  bearing: number,
  distanceMeters: number
) => {
  if (!distanceMeters) {
    return { lat, lng };
  }

  const angularDistance = distanceMeters / EARTH_RADIUS_METERS;
  const bearingRadians = toRadians(bearing);
  const latitudeRadians = toRadians(lat);
  const longitudeRadians = toRadians(lng);

  const projectedLatitude = Math.asin(
    clamp(
      Math.sin(latitudeRadians) * Math.cos(angularDistance) +
      Math.cos(latitudeRadians) *
      Math.sin(angularDistance) *
      Math.cos(bearingRadians),
      -1,
      1
    )
  );

  const projectedLongitude =
    longitudeRadians +
    Math.atan2(
      Math.sin(bearingRadians) *
      Math.sin(angularDistance) *
      Math.cos(latitudeRadians),
      Math.cos(angularDistance) -
      Math.sin(latitudeRadians) * Math.sin(projectedLatitude)
    );

  return {
    lat: toDegrees(projectedLatitude),
    lng: normalizeLongitude(toDegrees(projectedLongitude)),
  };
};

const projectTrackEvent = (
  event: InternalTrackEvent,
  elapsedMs: number
): InternalTrackEvent => {
  if (event.speed <= 0) {
    return event;
  }

  const distanceMeters = (event.speed * 1609.344 * elapsedMs) / 3_600_000;
  const projectedCoordinates = projectCoordinates(
    event.lat,
    event.lng,
    event.bearing,
    distanceMeters
  );

  return {
    ...event,
    lat: projectedCoordinates.lat,
    lng: projectedCoordinates.lng,
  };
};

const getDistanceMeters = (
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
) => {
  const latitudeDelta = toRadians(toLat - fromLat);
  const longitudeDelta = toRadians(toLng - fromLng);
  const startLatitude = toRadians(fromLat);
  const endLatitude = toRadians(toLat);

  const haversine =
    Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
    Math.cos(startLatitude) *
    Math.cos(endLatitude) *
    Math.sin(longitudeDelta / 2) *
    Math.sin(longitudeDelta / 2);

  return (
    2 *
    EARTH_RADIUS_METERS *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
};

const calculateBearing = (
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
) => {
  const startLatitude = toRadians(fromLat);
  const endLatitude = toRadians(toLat);
  const longitudeDelta = toRadians(toLng - fromLng);

  const y = Math.sin(longitudeDelta) * Math.cos(endLatitude);
  const x =
    Math.cos(startLatitude) * Math.sin(endLatitude) -
    Math.sin(startLatitude) *
    Math.cos(endLatitude) *
    Math.cos(longitudeDelta);

  return normalizeBearingValue(toDegrees(Math.atan2(y, x)));
};

const resolveClientBearing = (
  previousRealEvent: MotionTrackEvent | undefined,
  incomingEvent: InternalTrackEvent,
  fallbackBearing: number
) => {
  const previousBearing = normalizeBearingValue(
    previousRealEvent?.bearing,
    fallbackBearing
  );

  if (!previousRealEvent || incomingEvent.speed <= MIN_MOVING_SPEED_MPH) {
    return previousBearing;
  }

  const distanceMeters = getDistanceMeters(
    previousRealEvent.lat,
    previousRealEvent.lng,
    incomingEvent.lat,
    incomingEvent.lng
  );

  if (distanceMeters < MIN_BEARING_DISTANCE_METERS) {
    return previousBearing;
  }

  return calculateBearing(
    previousRealEvent.lat,
    previousRealEvent.lng,
    incomingEvent.lat,
    incomingEvent.lng
  );
};

const getCorrectionDurationMs = (
  previousRealEvent: MotionTrackEvent | undefined,
  nextEvent: InternalTrackEvent,
  receivedAtMs: number
) => {
  if (!previousRealEvent) {
    return 260;
  }

  const previousTimestampMs = getTimestampMs(previousRealEvent.timestamp);
  const nextTimestampMs = getTimestampMs(nextEvent.timestamp);
  const packetGapMs =
    nextTimestampMs > previousTimestampMs
      ? nextTimestampMs - previousTimestampMs
      : receivedAtMs - previousRealEvent.receivedAtMs;

  return clamp(packetGapMs * 0.05, 180, 420);
};

const getPlanarOffsetMeters = (
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
) => {
  const averageLatitudeRadians = toRadians((fromLat + toLat) / 2);

  return {
    east:
      toRadians(toLng - fromLng) *
      EARTH_RADIUS_METERS *
      Math.cos(averageLatitudeRadians),
    north: toRadians(toLat - fromLat) * EARTH_RADIUS_METERS,
  };
};

const getForwardOffsetMeters = (
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number,
  bearing: number
) => {
  const { east, north } = getPlanarOffsetMeters(fromLat, fromLng, toLat, toLng);
  const bearingRadians = toRadians(bearing);

  return east * Math.sin(bearingRadians) + north * Math.cos(bearingRadians);
};

const shouldKeepDisplayedAnchor = (
  displayedEvent: InternalTrackEvent,
  nextRealEvent: InternalTrackEvent
) =>
  getForwardOffsetMeters(
    displayedEvent.lat,
    displayedEvent.lng,
    nextRealEvent.lat,
    nextRealEvent.lng,
    nextRealEvent.bearing
  ) < -MIN_BEARING_DISTANCE_METERS;

const shouldAppendTrailPoint = (
  trailPath: TrailPoint[],
  nextPoint: TrailPoint
) => {
  const lastPoint = trailPath[trailPath.length - 1];
  if (!lastPoint) {
    return true;
  }

  return (
    Math.abs(lastPoint.lat - nextPoint.lat) > POSITION_EPSILON ||
    Math.abs(lastPoint.lng - nextPoint.lng) > POSITION_EPSILON
  );
};

const isIncomingEventNewer = (
  incomingTimestamp?: string,
  latestTimestamp?: string
) => {
  const incomingMs = getTimestampMs(incomingTimestamp);
  const latestMs = getTimestampMs(latestTimestamp);

  if (!incomingMs || !latestMs) {
    return true;
  }

  return incomingMs > latestMs;
};

export const useLiveTrackingMotion = ({
  connectionToken,
  fallbackTrackEvent,
  initialTrackEvent,
  vehicleId,
}: UseLiveTrackingMotionArgs): UseLiveTrackingMotionResult => {
  const [trackingLocation, setTrackingLocation] = useState<LiveTrackEvent>();
  const [trailPath, setTrailPath] = useState<TrailPoint[]>([]);
  const [updateReceived, setUpdateReceived] = useState(false);
  const [predictionExpired, setPredictionExpired] = useState(false);
  const [connectionTimedOut, setConnectionTimedOut] = useState(false);

  const latestRealEventRef = useRef<MotionTrackEvent>();
  const motionAnchorRef = useRef<MotionTrackEvent>();
  const behindPacketStreakRef = useRef(0);
  const displayedEventRef = useRef<InternalTrackEvent>();
  const correctionRef = useRef<CorrectionState>();
  const seedEventRef = useRef<InternalTrackEvent>();
  const animationFrameRef = useRef<number>();
  const waitStartedAtRef = useRef<number>();
  const updateReceivedRef = useRef(false);

  const initialSeed = useMemo(
    () => normalizeTrackEvent(initialTrackEvent),
    [initialTrackEvent]
  );
  const fallbackSeed = useMemo(
    () => normalizeTrackEvent(fallbackTrackEvent),
    [fallbackTrackEvent]
  );

  const emitTrackingLocation = useCallback((nextValue?: InternalTrackEvent) => {
    displayedEventRef.current = nextValue;
    const nextLocation = toLiveTrackEvent(nextValue);

    setTrackingLocation((previousValue) =>
      hasLiveTrackChanged(previousValue, nextLocation)
        ? nextLocation
        : previousValue
    );
  }, []);

  useEffect(() => {
    updateReceivedRef.current = updateReceived;
  }, [updateReceived]);

  useEffect(() => {
    const nextSeedEvent = initialSeed ?? fallbackSeed;
    const previousSeedEvent = seedEventRef.current;
    const seedChanged = hasLiveTrackChanged(
      toLiveTrackEvent(previousSeedEvent),
      toLiveTrackEvent(nextSeedEvent)
    );

    seedEventRef.current = nextSeedEvent;

    if (!updateReceived && seedChanged) {
      behindPacketStreakRef.current = 0;
      correctionRef.current = undefined;
      displayedEventRef.current = seedEventRef.current;
      emitTrackingLocation(seedEventRef.current);
      waitStartedAtRef.current = performance.now();
      setConnectionTimedOut(false);
      setPredictionExpired(false);
    }
  }, [emitTrackingLocation, fallbackSeed, initialSeed, updateReceived]);

  useEffect(() => {
    latestRealEventRef.current = undefined;
    motionAnchorRef.current = undefined;
    behindPacketStreakRef.current = 0;
    correctionRef.current = undefined;
    displayedEventRef.current = seedEventRef.current;
    waitStartedAtRef.current = performance.now();
    setTrailPath((previousTrail) =>
      previousTrail.length ? [] : previousTrail
    );
    setUpdateReceived(false);
    updateReceivedRef.current = false;
    setConnectionTimedOut(false);
    setPredictionExpired(false);
    emitTrackingLocation(seedEventRef.current);
  }, [connectionToken, emitTrackingLocation, vehicleId]);

  const commitIncomingEvent = useCallback(
    (incomingEvent: LiveTrackEvent) => {
      const previousRealEvent = latestRealEventRef.current;
      const normalizedEvent = normalizeTrackEvent(incomingEvent, {
        ...seedEventRef.current,
        ...previousRealEvent,
        ...displayedEventRef.current,
      });
      if (!normalizedEvent) {
        return;
      }

      if (
        !isIncomingEventNewer(
          normalizedEvent.timestamp,
          previousRealEvent?.timestamp
        )
      ) {
        return;
      }

      const receivedAtMs = performance.now();
      const currentDisplayedEvent =
        displayedEventRef.current ?? seedEventRef.current ?? normalizedEvent;
      const resolvedBearing = resolveClientBearing(
        previousRealEvent,
        normalizedEvent,
        currentDisplayedEvent.bearing
      );
      const nextRealEvent: InternalTrackEvent = {
        ...normalizedEvent,
        bearing: resolvedBearing,
      };
      const isFirstRealEvent = !previousRealEvent;
      const isBehindDisplayedAnchor =
        !isFirstRealEvent &&
        shouldKeepDisplayedAnchor(currentDisplayedEvent, nextRealEvent);
      const nextBehindPacketStreak = isBehindDisplayedAnchor
        ? behindPacketStreakRef.current + 1
        : 0;
      const keepDisplayedAnchor =
        isBehindDisplayedAnchor &&
        nextBehindPacketStreak < MAX_CONSECUTIVE_BEHIND_PACKETS;

      behindPacketStreakRef.current = keepDisplayedAnchor
        ? nextBehindPacketStreak
        : 0;

      latestRealEventRef.current = {
        ...nextRealEvent,
        receivedAtMs,
      };
      motionAnchorRef.current = {
        ...nextRealEvent,
        lat:
          isFirstRealEvent || !keepDisplayedAnchor
            ? nextRealEvent.lat
            : currentDisplayedEvent.lat,
        lng:
          isFirstRealEvent || !keepDisplayedAnchor
            ? nextRealEvent.lng
            : currentDisplayedEvent.lng,
        receivedAtMs,
      };
      correctionRef.current = {
        durationMs: getCorrectionDurationMs(
          previousRealEvent,
          nextRealEvent,
          receivedAtMs
        ),
        startAtMs: receivedAtMs,
        startEvent: currentDisplayedEvent,
      };
      setTrailPath((previousTrail) => {
        const nextPoint = { lat: nextRealEvent.lat, lng: nextRealEvent.lng };
        if (!shouldAppendTrailPoint(previousTrail, nextPoint)) {
          return previousTrail;
        }

        const nextTrail = [...previousTrail, nextPoint];
        return nextTrail.length > MAX_TRAIL_POINTS
          ? nextTrail.slice(-MAX_TRAIL_POINTS)
          : nextTrail;
      });

      setUpdateReceived(true);
      updateReceivedRef.current = true;
      setConnectionTimedOut(false);
      setPredictionExpired(false);
    },
    []
  );

  useEffect(() => {
    if (!connectionToken || typeof vehicleId !== "number") {
      return;
    }

    const apiUrl =
      process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
      "https://api.ai-eld.com";
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/signalr-hubs/live-track-hub`, {
        accessTokenFactory: () => connectionToken,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();
    let retryTimeout: number | undefined;
    let disposed = false;

    const sendVehicleId = () =>
      hubConnection
        .send("setVehicleId", `${vehicleId}`)
        .catch((error) =>
          console.warn(`Unable to set the vehicle ID to ${vehicleId}`, error)
        );

    const withinInitialWaitWindow = () =>
      performance.now() - (waitStartedAtRef.current ?? performance.now()) <
      LIVE_TRACKING_INITIAL_WAIT_WINDOW_MS;

    const clearRetry = () => {
      if (retryTimeout) {
        window.clearTimeout(retryTimeout);
        retryTimeout = undefined;
      }
    };

    const scheduleRetry = () => {
      if (disposed || updateReceivedRef.current || !withinInitialWaitWindow()) {
        return;
      }

      clearRetry();
      retryTimeout = window.setTimeout(() => {
        void startConnection();
      }, LIVE_TRACKING_INITIAL_RETRY_MS);
    };

    const startConnection = async () => {
      try {
        await hubConnection.start();
        clearRetry();
        await sendVehicleId();
      } catch (error) {
        console.error("Unable to start live tracking hub connection", error);
        scheduleRetry();
      }
    };

    hubConnection.on("updateLocation", commitIncomingEvent);
    hubConnection.onreconnected(sendVehicleId);
    hubConnection.onclose(() => {
      if (!updateReceivedRef.current) {
        scheduleRetry();
      }
    });

    void startConnection();

    return () => {
      disposed = true;
      clearRetry();
      hubConnection.off("updateLocation");
      hubConnection.stop();
    };
  }, [commitIncomingEvent, connectionToken, vehicleId]);

  useEffect(() => {
    const animate = () => {
      const latestRealEvent = latestRealEventRef.current;

      if (!latestRealEvent) {
        const seedEvent = seedEventRef.current;
        const waitStartedAt = waitStartedAtRef.current;
        const now = performance.now();

        emitTrackingLocation(seedEvent);
        setPredictionExpired(false);
        setConnectionTimedOut(
          !!seedEvent &&
          !updateReceivedRef.current &&
          !!waitStartedAt &&
          now - waitStartedAt >= LIVE_TRACKING_INITIAL_WAIT_WINDOW_MS
        );
        animationFrameRef.current = window.requestAnimationFrame(animate);
        return;
      }

      const motionAnchor = motionAnchorRef.current ?? latestRealEvent;
      const now = performance.now();
      const elapsedSinceUpdate = Math.max(0, now - latestRealEvent.receivedAtMs);

      if (elapsedSinceUpdate > LIVE_TRACKING_MAX_PREDICTION_WINDOW_MS) {
        correctionRef.current = undefined;
        emitTrackingLocation(latestRealEvent ?? seedEventRef.current);
        setPredictionExpired(true);
        setConnectionTimedOut(false);
        animationFrameRef.current = window.requestAnimationFrame(animate);
        return;
      }

      const projectedTarget = projectTrackEvent(motionAnchor, elapsedSinceUpdate);
      const correction = correctionRef.current;
      let nextDisplayedEvent: InternalTrackEvent;
      if (!correction) {
        nextDisplayedEvent = projectedTarget;
      } else {
        const progress = clamp(
          (now - correction.startAtMs) / correction.durationMs,
          0,
          1
        );
        nextDisplayedEvent = interpolateTrackEvent(
          correction.startEvent,
          projectedTarget,
          easeOutCubic(progress)
        );

        if (progress >= 1) {
          correctionRef.current = undefined;
        }
      }

      emitTrackingLocation(nextDisplayedEvent);
      setPredictionExpired(false);
      setConnectionTimedOut(false);
      animationFrameRef.current = window.requestAnimationFrame(animate);
    };

    animationFrameRef.current = window.requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [emitTrackingLocation]);

  return {
    connectionTimedOut,
    trackingLocation,
    trailPath,
    updateReceived,
    predictionExpired,
  };
};
